import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { KIDS, KIDS_AT_SUNSET, KIDS_PHILLY } from "../../data/kids";
import KidCardMedia from "../../components/KidCardMedia";
import SEO from "../../components/SEO";

export default function MeetTheKids() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="meet-the-kids-page">
      <SEO
        path="/meet-the-kids"
        title="Meet the Kids | Foster Care Awareness 365 Featured Profiles"
        description="Four representative foster youth profiles — Malik, Ariana, Jayden, and Sophia — drawn from national trends in U.S. foster care. Real hopes, real interests, real waiting. Discover how you can be the one who shows up."
      />
      {/* Hero — cinematic dark-scrim treatment, white text on tinted background */}
      <section className="relative overflow-hidden border-b border-fc-border bg-fc-ink">
        <div
          className="absolute inset-0 ken-burns"
          style={{ backgroundImage: `url(${KIDS_AT_SUNSET})`, backgroundSize: "cover", backgroundPosition: "center 35%" }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/25" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-b from-transparent to-black/70" aria-hidden="true" />
        <div className="container-xl relative pt-20 pb-20 md:pt-24 md:pb-24">
          <div className="overline text-fc-accent mb-5">Featured Profiles · Real Stories & Dreams of a Family</div>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-5xl text-white">
            Meet the children behind <span className="italic text-fc-accent">the cause.</span>
          </h1>
          <p className="mt-6 text-white/85 max-w-3xl leading-relaxed">
            Discover the faces and dreams behind the statistics. Learn their hopes, interests, and how you can be the one who gives them a forever family.
          </p>
        </div>
      </section>

      <section className="container-xl py-16">
        <div className="border-l-4 border-fc-accent pl-6 py-2 bg-fc-surface/60 mb-12">
          <p className="text-sm md:text-base text-fc-ink leading-relaxed">
            <strong>The Featured Child Profiles are representative profiles</strong> of children in foster care (2 males, 2 females) that reflect trends and realities across the U.S. foster care system. These are not meant to stereotype or generalize, but to serve as archetypes based on common characteristics seen in national data and casework insights.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {KIDS.map((k) => (
            <Link key={k.slug} to={`/meet-the-kids/${k.slug}`} className="group block border border-fc-border card-hover" data-testid={`kids-grid-${k.slug}`}>
              <div className="grid grid-cols-2">
                <KidCardMedia
                  image={k.hero}
                  alt={k.name}
                  youtubeId={k.youtube_id}
                  className="aspect-[3/4]"
                  testId={`kids-grid-media-${k.slug}`}
                />
                <div className="p-6 flex flex-col justify-between">
                  <div>
                    <div className="overline text-fc-accent mb-2">Age {k.age}</div>
                    <div className="font-display text-3xl mb-3">{k.name}</div>
                    <div className="italic text-fc-muted leading-snug mb-4">"{k.tagline}"</div>
                    <p className="text-sm text-fc-muted leading-relaxed line-clamp-5">{k.short}</p>
                  </div>
                  <div className="flex items-center gap-2 overline text-fc-ink mt-5">
                    Read Full Profile <ArrowUpRight size={14} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-fc-surface border-t border-fc-border py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.25]">
          <img src={KIDS_PHILLY} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-fc-surface/40" />
        </div>
        <div className="container-xl text-center max-w-3xl relative">
          <div className="overline text-fc-muted mb-4">What You Can Do</div>
          <h2 className="font-display text-3xl md:text-5xl mb-6 leading-tight">
            Every child deserves to be seen. <em className="text-fc-accent">Show up.</em>
          </h2>
          <p className="text-fc-muted leading-relaxed mb-8">
            Share a profile, sponsor a sibling visit, become a mentor, or explore fostering. The smallest actions multiply — because kids can't wait another year.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/get-involved" className="btn-primary" data-testid="kids-cta-give">Ways to Give</Link>
            <Link to="/everyday-awareness" className="btn-outline" data-testid="kids-cta-challenge">Join the #Foster365 Challenge</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
